import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  FormGroup,
  Slider,
  LinearProgress,
  Stack
} from '@mui/material';
import { ArrowBack, ArrowForward } from '@mui/icons-material';

/**
 * GamificationForm Component
 *
 * Multi-step form that collects user profile data through
 * gamified questions. Questions are configurable and
 * responses feed into AI matching engine.
 *
 * Props:
 * - insuranceType: 'health', 'auto', 'life', etc.
 * - onComplete: (userProfile) => void
 * - questions: array of question configs (optional, uses defaults)
 */
const GamificationForm = ({
  insuranceType,
  onComplete,
  questions: customQuestions
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [userProfile, setUserProfile] = useState({
    product_type: insuranceType,
    is_switching: null,
    current_premium: null,
    budget_monthly: null,
    family_size: 1,
    coverage_level: null,
    priorities: [],
    specific_needs: {},
    age: null,
    has_pre_existing: null
  });

  // Default question configuration (backend-configurable in production)
  const defaultQuestions = [
    {
      id: 'is_switching',
      type: 'radio',
      question: 'Are you currently insured?',
      questionAr: 'هل لديك تأمين حالياً؟',
      options: [
        { value: true, label: 'Yes (I want to compare/switch)', labelAr: 'نعم (أريد المقارنة/التبديل)' },
        { value: false, label: 'No (First-time buyer)', labelAr: 'لا (مشتري لأول مرة)' }
      ],
      field: 'is_switching',
      required: true
    },
    {
      id: 'current_premium',
      type: 'number',
      question: 'How much are you paying now?',
      questionAr: 'كم تدفع الآن؟',
      field: 'current_premium',
      required: true,
      showIf: (profile) => profile.is_switching === true,
      placeholder: 'Annual premium (e.g., 1200)',
      helperText: 'Enter your current annual premium in USD'
    },
    {
      id: 'budget',
      type: 'slider',
      question: "What's your monthly budget?",
      questionAr: 'ما هي ميزانيتك الشهرية؟',
      field: 'budget_monthly',
      required: true,
      showIf: (profile) => profile.is_switching === false,
      min: 0,
      max: 300,
      step: 10,
      marks: [
        { value: 0, label: '$0' },
        { value: 100, label: '$100' },
        { value: 200, label: '$200' },
        { value: 300, label: '$300+' }
      ]
    },
    {
      id: 'family_size',
      type: 'radio',
      question: 'Who are you covering?',
      questionAr: 'من تريد تغطيته؟',
      options: [
        { value: 1, label: 'Just me', labelAr: 'أنا فقط' },
        { value: 2, label: 'Me + Spouse', labelAr: 'أنا + الزوج/الزوجة' },
        { value: 3, label: 'Me + Spouse + 1 Child', labelAr: 'أنا + الزوج/الزوجة + طفل واحد' },
        { value: 4, label: 'Me + Spouse + 2 Children', labelAr: 'أنا + الزوج/الزوجة + طفلان' },
        { value: 5, label: 'Family of 5+', labelAr: 'عائلة من 5+' }
      ],
      field: 'family_size',
      required: true
    },
    {
      id: 'coverage_level',
      type: 'radio',
      question: 'What level of coverage do you want?',
      questionAr: 'ما مستوى التغطية الذي تريده؟',
      options: [
        {
          value: 'basic',
          label: 'Basic',
          labelAr: 'أساسي',
          description: 'Inpatient + Emergency (~$500/year)',
          descriptionAr: 'المرضى الداخليون + الطوارئ (~$500/سنة)'
        },
        {
          value: 'standard',
          label: 'Standard',
          labelAr: 'قياسي',
          description: '+ Outpatient + Lab Tests (~$800/year)',
          descriptionAr: '+ العيادات الخارجية + الفحوصات (~$800/سنة)'
        },
        {
          value: 'comprehensive',
          label: 'Comprehensive',
          labelAr: 'شامل',
          description: '+ Dental + Optical + Maternity (~$1,200/year)',
          descriptionAr: '+ الأسنان + البصريات + الولادة (~$1,200/سنة)'
        }
      ],
      field: 'coverage_level',
      required: true
    },
    {
      id: 'priorities',
      type: 'checkbox',
      question: 'What matters most to you? (Pick top 3)',
      questionAr: 'ما الأهم بالنسبة لك؟ (اختر أعلى 3)',
      options: [
        { value: 'price', label: 'Lowest price', labelAr: 'أقل سعر' },
        { value: 'network', label: 'Best hospital network', labelAr: 'أفضل شبكة مستشفيات' },
        { value: 'claims_speed', label: 'Fast claim processing', labelAr: 'معالجة سريعة للمطالبات' },
        { value: 'reputation', label: 'Insurer reputation', labelAr: 'سمعة شركة التأمين' },
        { value: 'coverage', label: 'High coverage limits', labelAr: 'حدود تغطية عالية' },
        { value: 'dental_optical', label: 'Dental & optical included', labelAr: 'الأسنان والبصريات متضمنة' }
      ],
      field: 'priorities',
      required: true,
      maxSelections: 3
    },
    {
      id: 'age',
      type: 'number',
      question: 'What is your age? (Optional)',
      questionAr: 'كم عمرك؟ (اختياري)',
      field: 'age',
      required: false,
      placeholder: '32',
      helperText: 'Used for more accurate pricing',
      min: 18,
      max: 100
    },
    {
      id: 'pre_existing',
      type: 'radio',
      question: 'Do you have any pre-existing medical conditions? (Optional)',
      questionAr: 'هل لديك أي حالات طبية موجودة مسبقاً؟ (اختياري)',
      options: [
        { value: false, label: 'No', labelAr: 'لا' },
        { value: true, label: 'Yes', labelAr: 'نعم' },
        { value: null, label: 'Prefer not to say', labelAr: 'أفضل عدم الإفصاح' }
      ],
      field: 'has_pre_existing',
      required: false,
      helperText: 'Helps us find plans that cover your needs'
    }
  ];

  const questions = customQuestions || defaultQuestions;

  // Filter questions based on showIf conditions
  const visibleQuestions = questions.filter((q) =>
    !q.showIf || q.showIf(userProfile)
  );

  const currentQuestion = visibleQuestions[currentStep];
  const progress = visibleQuestions.length ? ((currentStep + 1) / visibleQuestions.length) * 100 : 0;

  const handleNext = () => {
    if (currentStep < visibleQuestions.length - 1) {
      setCurrentStep((prev) => prev + 1);
    } else {
      onComplete(userProfile);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  const handleChange = (field, value) => {
    setUserProfile((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCheckboxChange = (field, value, checked) => {
    setUserProfile((prev) => {
      const currentValues = prev[field] || [];
      const newValues = checked
        ? [...currentValues, value]
        : currentValues.filter((v) => v !== value);

      if (currentQuestion.maxSelections && newValues.length > currentQuestion.maxSelections) {
        return prev;
      }

      return {
        ...prev,
        [field]: newValues
      };
    });
  };

  const isCurrentStepValid = () => {
    if (!currentQuestion?.required) return true;

    const value = userProfile[currentQuestion.field];

    if (currentQuestion.type === 'checkbox') {
      return value && value.length > 0;
    }

    return value !== null && value !== undefined && value !== '';
  };

  const renderQuestion = () => {
    if (!currentQuestion) return null;

    switch (currentQuestion.type) {
      case 'radio':
        return (
          <RadioGroup
            value={
              userProfile[currentQuestion.field] === null
                ? 'null'
                : String(userProfile[currentQuestion.field] ?? '')
            }
            onChange={(e) => {
              const option = currentQuestion.options.find(
                (opt) => String(opt.value) === e.target.value
              );
              handleChange(currentQuestion.field, option?.value);
            }}
          >
            {currentQuestion.options.map((option) => (
              <FormControlLabel
                key={String(option.value)}
                value={String(option.value)}
                control={<Radio />}
                label={
                  <Box>
                    <Typography variant="body1">{option.label}</Typography>
                    {option.description && (
                      <Typography variant="body2" color="text.secondary">
                        {option.description}
                      </Typography>
                    )}
                  </Box>
                }
              />
            ))}
          </RadioGroup>
        );

      case 'checkbox':
        return (
          <FormGroup>
            {currentQuestion.options.map((option) => (
              <FormControlLabel
                key={option.value}
                control={
                  <Checkbox
                    checked={(userProfile[currentQuestion.field] || []).includes(option.value)}
                    onChange={(e) =>
                      handleCheckboxChange(
                        currentQuestion.field,
                        option.value,
                        e.target.checked
                      )
                    }
                  />
                }
                label={option.label}
              />
            ))}
            {currentQuestion.maxSelections && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                Selected: {(userProfile[currentQuestion.field] || []).length} / {currentQuestion.maxSelections}
              </Typography>
            )}
          </FormGroup>
        );

      case 'number':
        return (
          <TextField
            fullWidth
            type="number"
            value={userProfile[currentQuestion.field] ?? ''}
            onChange={(e) => {
              const num = parseFloat(e.target.value);
              handleChange(currentQuestion.field, Number.isFinite(num) ? num : null);
            }}
            placeholder={currentQuestion.placeholder}
            helperText={currentQuestion.helperText}
            inputProps={{
              min: currentQuestion.min,
              max: currentQuestion.max
            }}
            InputProps={{
              ...(currentQuestion.field === 'current_premium' && {
                startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>
              })
            }}
          />
        );

      case 'slider':
        return (
          <Box sx={{ px: 2, pt: 2 }}>
            <Slider
              value={userProfile[currentQuestion.field] ?? currentQuestion.min ?? 0}
              onChange={(e, value) => handleChange(currentQuestion.field, value)}
              min={currentQuestion.min ?? 0}
              max={currentQuestion.max ?? 100}
              step={currentQuestion.step ?? 1}
              marks={currentQuestion.marks}
              valueLabelDisplay="on"
              valueLabelFormat={(value) => `$${value}`}
            />
            <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 2 }}>
              ~${userProfile[currentQuestion.field] ?? currentQuestion.min ?? 0}/month
            </Typography>
          </Box>
        );

      default:
        return null;
    }
  };

  if (!currentQuestion) return null;

  return (
    <Paper elevation={3} sx={{ p: 4, maxWidth: 600, mx: 'auto' }}>
      {/* Progress Bar */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Question {currentStep + 1} of {visibleQuestions.length}
          </Typography>
          <Typography variant="caption" color="primary" fontWeight="bold">
            {Math.round(progress)}% Complete
          </Typography>
        </Box>
        <LinearProgress variant="determinate" value={progress} sx={{ height: 8, borderRadius: 4 }} />
      </Box>

      {/* Question */}
      <Typography variant="h5" gutterBottom>
        {currentQuestion.question}
      </Typography>

      {currentQuestion.questionAr && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {currentQuestion.questionAr}
        </Typography>
      )}

      {/* Answer Input */}
      <Box sx={{ my: 3 }}>
        {renderQuestion()}
      </Box>

      {/* Navigation */}
      <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBack />}
          onClick={handleBack}
          disabled={currentStep === 0}
        >
          Back
        </Button>
        <Button
          variant="contained"
          endIcon={<ArrowForward />}
          onClick={handleNext}
          disabled={!isCurrentStepValid()}
          fullWidth
        >
          {currentStep === visibleQuestions.length - 1 ? 'See My Matches' : 'Continue'}
        </Button>
      </Stack>
    </Paper>
  );
};

export default GamificationForm;
